import Icon from '@/components/icons/Icon';
import { SEARCH_CONTEXT } from '@/features/searchV2/constants/tracking';
import { isAndroidPhone, isIPhone, isServer, isSistaminuten, setCookie, getCookie } from '@/helpers';
import { getScreenNameFromPath } from '@/helpers/analyticsTS';
import { _t } from '@/locale';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button } from '../forms/buttons';
import { ListItem, ListItemContent } from '../lists';
import styles from './AppPromotionCard.module.scss';
import Card from './Card';
const LOCAL_STORAGE_BASE_KEY = 'sistaminutenPromoDismissed';

const whiteListedPages = ['home', 'search_results', 'my_bookings_upcoming', 'my_bookings_finished', 'favorites'];

export const allowSistaminutenPromotionCard = (locationPathname: string | undefined) => {
  if (isServer) return false;

  if (!(isIPhone() || isAndroidPhone())) {
    return false;
  }

  const screenName = getScreenNameFromPath(locationPathname);
  if (!whiteListedPages.some((page) => page === screenName) || (screenName === 'home' && locationPathname !== '/')) {
    return false;
  }

  /* do not show if already shown in current session */
  if (Boolean(sessionStorage.getItem(LOCAL_STORAGE_BASE_KEY))) {
    return false;
  }

  /* Sista Minuten banner is capped at 1 impression if clicked, 2 impressions if closed */
  const cardImpressions = parseInt(getCookie(LOCAL_STORAGE_BASE_KEY)) || 0;
  return cardImpressions < 2;
};

export const checkIfDismissedInSession = () => {
  if (isServer) return true;
  if (Boolean(sessionStorage.getItem(LOCAL_STORAGE_BASE_KEY))) {
    return true;
  }

  return false;
};

const SistaminutenPromotionCard = () => {
  const location = useLocation();
  const history = useHistory();

  const [hasDismissed, setHasDismissed] = useState(false);

  const [timerTriggered, setTimerTriggered] = useState(false);
  const showCard = !isServer && !isSistaminuten() && !hasDismissed && timerTriggered;

  useEffect(() => {
    const setNewTimeout = () => {
      clearTimeout(timer);

      timer = setTimeout(() => {
        setTimerTriggered(true);
      }, 2000);
    };

    let timer = setTimeout(() => {
      setTimerTriggered(true);
    }, 2000);

    setNewTimeout();

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleDismiss = () => {
    setHasDismissed(true);
    sessionStorage.setItem(LOCAL_STORAGE_BASE_KEY, 'dismissed');
    const alreadyShown = parseInt(getCookie(LOCAL_STORAGE_BASE_KEY)) || 0;
    setCookie(LOCAL_STORAGE_BASE_KEY, `${alreadyShown + 1}`, {
      path: '/',
      expires: new Date(Date.now() + 30 * 86400 * 1000),
    });
  };

  const handleSistaminutenClick = () => {
    sessionStorage.setItem(LOCAL_STORAGE_BASE_KEY, 'dismissed');
    setCookie(LOCAL_STORAGE_BASE_KEY, '2', { path: '/', expires: new Date(Date.now() + 30 * 86400 * 1000) });
    history.push({ pathname: '/sistaminuten', state: { searchContext: SEARCH_CONTEXT.SISTAMINUTEN_PROMOTION_CARD } });
  };

  return showCard ? (
    <>
      <div className={`z-10003 fixed h-screen w-screen bg-black opacity-50`}></div>
      <Card bottom closable={false} className={`z-10004 fixed bottom-0 ${styles.slideInBottom}`}>
        <div className="gap-lg flex flex-col">
          <div className="gap-xl flex min-w-full flex-col">
            <div className="gap-lg flex">
              <div>
                <div className="px-sm py-xxs bg-bg_notice_bold rounded-md">
                  <Icon variant="smt" size="lg" color="white" />
                </div>
              </div>
              <div className="gap-sm flex flex-col">
                <h2 className="text-lg font-bold">{_t('components.elements.Card.SistaminutenPromotionCard.title')}</h2>
              </div>
            </div>
            <ul className="gap-sm flex flex-col">
              <ListItem
                className="!py-[0]"
                leftPadding={false}
                leftSlot={<Icon color="brand-500" variant="checkbox-checked-round" size="sm" />}>
                <ListItemContent
                  subTitle={_t('components.elements.Card.SistaminutenPromotionCard.contentList.item1')}
                />
              </ListItem>
              <ListItem
                className="!py-[0]"
                leftPadding={false}
                leftSlot={<Icon color="brand-500" variant="checkbox-checked-round" size="sm" />}>
                <ListItemContent
                  subTitle={_t('components.elements.Card.SistaminutenPromotionCard.contentList.item2')}
                />
              </ListItem>
              <ListItem
                className="!py-[0]"
                leftPadding={false}
                leftSlot={<Icon color="brand-500" variant="checkbox-checked-round" size="sm" />}>
                <ListItemContent
                  subTitle={_t('components.elements.Card.SistaminutenPromotionCard.contentList.item3')}
                />
              </ListItem>
            </ul>
          </div>
          <div className="gap-sm flex flex-col">
            <Button
              variant="primary"
              onClick={handleSistaminutenClick}
              block
              label={_t('components.elements.Card.SistaminutenPromotionCard.cta')}
              size="lg"
            />
            <Button
              variant="link"
              onClick={handleDismiss}
              block
              label={_t('components.elements.Card.SistaminutenPromotionCard.link')}
              size="sm"
            />
          </div>
        </div>
      </Card>
    </>
  ) : null;
};

export default SistaminutenPromotionCard;
